import { useState, useRef } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Camera, X } from 'lucide-react'

// ANU-88 — Photo Upload component.
// Camera-first on mobile (input capture=environment), falls back to picker.
// Client-side compresses via Canvas API (no external libs): 800px longest
// edge, JPEG quality 0.80. Uploads to private `event-media` bucket and
// returns a 1hr signed URL plus storage path via onUpload(url, path).
// FLARE green theme to match the rest of the app — spec said cyan but the
// app is monochrome green across PlayerView/CommandCenter/etc. so we hold
// the line.
export default function PhotoUpload({
  onUpload,
  onUploadStart,
  folder,
  label = 'Add Photo',
  existingUrl,
  disabled = false,
  maxSizeMB = 1,
}) {
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [thumb, setThumb] = useState(existingUrl || null)

  // 800px longest edge, JPEG 0.80 — Canvas API only, per spec.
  const compress = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.onload = (e) => {
      const img = new Image()
      img.onerror = () => reject(new Error('Failed to decode image'))
      img.onload = () => {
        const MAX = 800
        const ratio = Math.min(MAX / img.width, MAX / img.height, 1)
        const w = Math.max(1, Math.round(img.width * ratio))
        const h = Math.max(1, Math.round(img.height * ratio))
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, w, h)
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Canvas encode failed')),
          'image/jpeg',
          0.80
        )
      }
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
  })

  const handleFile = async (e) => {
    setError('')
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('File must be an image')
      return
    }
    setBusy(true)
    onUploadStart?.()
    try {
      const blob = await compress(file)
      // 800px/0.80 reliably lands well under 1 MB; warn loudly if not so we
      // catch upstream issues instead of silently shipping large blobs.
      if (blob.size > maxSizeMB * 1024 * 1024) {
        console.warn(`PhotoUpload: compressed blob exceeded ${maxSizeMB}MB (${blob.size}b)`)
      }
      const path = `${folder}/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('event-media')
        .upload(path, blob, { contentType: 'image/jpeg', upsert: false })
      if (upErr) throw upErr
      const { data: signed, error: signErr } = await supabase.storage
        .from('event-media')
        .createSignedUrl(path, 3600)
      if (signErr) throw signErr
      setThumb(signed.signedUrl)
      onUpload?.(signed.signedUrl, path)
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const handleRemove = () => {
    // Per spec: do NOT delete from storage on remove. The row referencing
    // this path (war_crimes, kill, etc.) is the source of truth; orphan
    // cleanup is a separate job.
    setThumb(null)
    setError('')
    onUpload?.(null, null)
  }

  if (thumb) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          position: 'relative',
          width: 80,
          height: 80,
          border: '1px solid rgba(0,255,102,.3)',
          background: '#0a140a',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={disabled}
          style={{
            background: 'rgba(255,42,42,.06)',
            border: '1px solid rgba(255,42,42,.4)',
            color: '#ff6a6a',
            padding: '8px 12px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 44,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <X size={12} /> REMOVE
        </button>
      </div>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || busy}
        style={{
          width: '100%',
          padding: '12px 14px',
          background: 'rgba(0,255,102,.06)',
          border: '1px solid rgba(0,255,102,.4)',
          color: '#00ff66',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          textShadow: '0 0 4px rgba(0,255,102,.3)',
          cursor: (disabled || busy) ? 'not-allowed' : 'pointer',
          opacity: (disabled || busy) ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 44,
        }}
      >
        <Camera size={14} />
        {busy ? 'UPLOADING...' : label.toUpperCase()}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        disabled={disabled || busy}
        style={{ display: 'none' }}
      />
      {error && (
        <p style={{
          color: '#ff6a6a',
          fontSize: 11,
          marginTop: 6,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {error}
        </p>
      )}
    </div>
  )
}
